import comm from 'Modules/comm'
import * as endpoint from 'Modules/constants'
import util from 'Modules/util'

const p = endpoint.PIPELINE_PROXY
const t = endpoint.TRANSFORM_GROUP_PROXY

const v3unwrap = async (method, url, payload, options) => {
  try {
    const response = await comm[method](url, payload, options)
    const { data = {}, ...rest } = response ?? {}
    if (typeof data === 'object' && 'meta' in data && 'data' in data) return { data: data.data, ...rest }
    return response
  } catch (e) {
    throw e
  }
}

const commV3 = (base) => (
  {
    get: async (url) => v3unwrap('get', `${base}${url}`),
    post: async (url, payload, options) => v3unwrap('post', `${base}${url}`, payload, options),
    put: async (url, payload) => v3unwrap('put', `${base}${url}`, payload),
    patch: async (url, payload) => v3unwrap('patch', `${base}${url}`, payload),
    delete: async (url, payload) => v3unwrap('delete', `${base}${url}`, payload)
  }
)

export default {
  pipelines: {
    query: () => commV3(p).get(''),
    get: pipelineId => commV3(p).get(`/${pipelineId}`),
    create: payload => commV3(p).post('', payload),
    update: (pipelineId, payload) => commV3(p).put(`/${pipelineId}`, payload),
    delete: pipelineId => commV3(p).delete(`/${pipelineId}`),
    publish: (pipelineId, query) => commV3(p).post(`/${pipelineId}/publish?${util.queryStringify(query)}`),
    revert: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/revert`, payload),
    processingStatus: (pipelineId, payload) => commV3(p).put(`/${pipelineId}/processing-status`, payload),
    simulate: ({ pipelineId, sourceId }, payload) => commV3(p).post(`/${pipelineId}/simulate/${sourceId}`, payload),
    tap: ({ pipelineId, nodeId }, payload, options) => commV3(p).post(`/${pipelineId}/tap/${nodeId}`, payload, options),
    export: ({ pipelineId }) => commV3(p).post(`/${pipelineId}/export`),
    createWithComponents: (payload) => commV3(p).post('/with-linked-components', payload),
    revisions: {
      latest: pipelineId => commV3(p).get(`/${pipelineId}/revision/latest`),
      list: ({ pipelineId }) => commV3(p).get(`/${pipelineId}/revision`),
      get: ({ pipelineId, revisionId }) => commV3(p).get(`/${pipelineId}/revision/${revisionId}`)
    },
    sources: {
      add: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/source`, payload),
      update: ({ pipelineId, nodeId }, payload) => commV3(p).put(`/${pipelineId}/source/${nodeId}`, payload),
      delete: ({ pipelineId, nodeId }) => commV3(p).delete(`/${pipelineId}/source/${nodeId}`),
      samples: {
        query: ({ pipelineId, sourceId }) => commV3(p).get(`/${pipelineId}/source/${sourceId}/sample`),
        get: ({ pipelineId, sourceId, sampleId }) => commV3(p).get(`/${pipelineId}/source/${sourceId}/sample/${sampleId}`),
        add: ({ pipelineId, sourceId }, payload) => commV3(p).post(`/${pipelineId}/source/${sourceId}/sample`, payload),
        update: ({ pipelineId, sourceId, sampleId }, payload) => commV3(p).put(`/${pipelineId}/source/${sourceId}/sample/${sampleId}`, payload),
        delete: ({ pipelineId, sourceId, sampleId }) => commV3(p).delete(`/${pipelineId}/source/${sourceId}/sample/${sampleId}`)
      }
    },
    transforms: {
      add: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/transform`, payload),
      update: ({ pipelineId, nodeId }, payload) => commV3(p).put(`/${pipelineId}/transform/${nodeId}`, payload),
      delete: ({ pipelineId, nodeId }) => commV3(p).delete(`/${pipelineId}/transform/${nodeId}`),
      classifier: {
        list: () => commV3(p).get('/transform/classifier/analysis')
      },
      createFromProfiler: (pipelineId, payload, query) => commV3(p).post(`/${pipelineId}/log-volume-reduction?${util.queryStringify(query)}`, payload)
    },
    transform_groups: {
      add: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/transform-group`, payload),
      update: ({ pipelineId, nodeId }, payload) => commV3(p).put(`/${pipelineId}/transform-group/${nodeId}`, payload),
      delete: ({ pipelineId, nodeId }) => commV3(p).delete(`/${pipelineId}/transform-group/${nodeId}`)
    },
    sinks: {
      add: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/sink`, payload),
      update: ({ pipelineId, nodeId }, payload) => commV3(p).put(`/${pipelineId}/sink/${nodeId}`, payload),
      delete: ({ pipelineId, nodeId }) => commV3(p).delete(`/${pipelineId}/sink/${nodeId}`)
    },
    template: {
      add: (payload) => commV3(p).post('/pre-built', payload)
    },
    eventMetrics: {
      query: (pipelineId, query) => commV3(p).get(`/${pipelineId}/event_metrics?${util.queryStringify(query)}`)
    }
  },
  // Note Transform groups uses a different proxy than the normal pipeline endpoints
  // Note the t instead of p ;)
  transformGroups: {
    query: () => commV3(t).get(''),
    get: pipelineId => commV3(t).get(`/${pipelineId}`),
    create: payload => commV3(t).post('', payload),
    update: (pipelineId, payload) => commV3(t).put(`/${pipelineId}`, payload),
    delete: pipelineId => commV3(t).delete(`/${pipelineId}`),
    publish: (pipelineId, query) => commV3(t).post(`/${pipelineId}/publish?${util.queryStringify(query)}`),
    simulate: ({ pipelineId }, payload) => commV3(t).post(`/${pipelineId}/simulate`, payload),
    createFromPipeline: (payload) => commV3(t).post('/from-pipeline', payload),
    revisions: {
      latest: pipelineId => commV3(t).get(`/${pipelineId}/revision/latest`),
      list: ({ pipelineId }) => commV3(t).get(`/${pipelineId}/revision`),
      get: ({ pipelineId, revisionId }) => commV3(t).get(`/${pipelineId}/revision/${revisionId}`)
    },
    transforms: {
      add: ({ pipelineId }, payload) => commV3(t).post(`/${pipelineId}/transform`, payload),
      update: ({ pipelineId, nodeId }, payload) => commV3(t).put(`/${pipelineId}/transform/${nodeId}`, payload),
      delete: ({ pipelineId, nodeId }) => commV3(t).delete(`/${pipelineId}/transform/${nodeId}`)
    }
  },
  classifier: {
    query: (query) => commV3(p).get(`/classifier/analysis?${util.queryStringify(query)}`),
    patch: (analysisId, payload, query) => commV3(p).patch(`/classifier/analysis/${analysisId}?${util.queryStringify(query)}`, payload),
    fields: (analysisId) => commV3(p).get(`/classifier/${analysisId}/fields`),
    field: (analysisId, field_name) => commV3(p).get(`/classifier/${analysisId}/fields/${field_name}`)
  },
  gatewayRoutes: {
    query: (query) => commV3(p).get(`/gateway-route?${util.queryStringify(query)}`),
    get: (gatewayRouteId) => commV3(p).get(`/gateway-route/${gatewayRouteId}`),
    add: (payload) => commV3(p).post('/gateway-route', payload),
    update: (gatewayRouteId, payload) => commV3(p).put(`/gateway-route/${gatewayRouteId}`, payload),
    delete: (gatewayRouteId) => commV3(p).delete(`/gateway-route/${gatewayRouteId}`),
    patch: (gatewayRouteId, payload) => commV3(p).patch(`/gateway-route/${gatewayRouteId}`, payload),
    accessKeys: {
      create: ({ gatewayRouteId }, payload) =>
        commV3(p).post(`/gateway-route/${gatewayRouteId}/access-key`, payload),
      delete: ({ gatewayRouteId, id }) => commV3(p).delete(`/gateway-route/${gatewayRouteId}/access-key/${id}`)
    },
    signingKeys: {
      create: ({ gatewayRouteId }, payload) =>
        commV3(p).put(`/gateway-route/${gatewayRouteId}/signing-key`, payload),
      delete: ({ gatewayRouteId, id }) => commV3(p).delete(`/gateway-route/${gatewayRouteId}/signing-key/${id}`)
    }
  },
  grokPatterns: {
    query: () => commV3(p).get('/grok-pattern'),
    simulate: (payload) => commV3(p).post('/grok-pattern/simulate', payload)
  },
  regexPatterns: {
    simulate: (payload) => commV3(p).post('/regex-pattern/simulate', payload)
  },
  timestampFormat: {
    simulate: (payload) => commV3(p).post('/timestamp-format/simulate', payload)
  },
  nodes: {
    query: () => commV3(p).get('/node')
  },
  auditEvents: {
    query: (query) => commV3(p).get(`/audit-event?${util.queryStringify(query)}`)
  },
  metrics: {
    // See api docs for query params.
    usage: {
      query: query => commV3(p).get(`/metric/usage?${util.queryStringify(query)}`),
      profile: {
        query: query => commV3(p).get(`/metric/usage/profile?${util.queryStringify(query)}`),
        logClustering: {
          query: query => commV3(p).get(`/metric/usage/profile/log-clustering?${util.queryStringify(query)}`),
          samples: {
            get: (query) => commV3(p).get(`/metric/usage/profile/log-clustering/samples?${util.queryStringify(query)}`)
          }
        }
      }
    },
    health: {
      query: query => commV3(p).get(`/metric/health?${util.queryStringify(query)}`)
    }
  },
  // Node only alerts
  nodeAlerts: {
    // swimlane is source, transform, or sink
    list: ({ pipelineId }, query) => commV3(p).get(`/${pipelineId}/alert?${util.queryStringify(query)}`),
    add: ({ pipelineId, swimlane, nodeId }, payload) => commV3(p).post(`/${pipelineId}/${swimlane}/${nodeId}/alert`, payload),
    update: ({ pipelineId, swimlane, nodeId, alertId }, payload) => commV3(p).put(`/${pipelineId}/${swimlane}/${nodeId}/alert/${alertId}`, payload),
    delete: ({ pipelineId, swimlane, nodeId, alertId, componentAlertType }) => commV3(p).delete(`/${pipelineId}/${swimlane}/${nodeId}/alert/${alertId}?component_alert_type=${componentAlertType}`)
  },
  // these alerts are for the entire pipeline
  thresholdAlerts: {
    list: ({ pipelineId }) => commV3(p).get(`/${pipelineId}/usage-threshold-alert`),
    get: ({ pipelineId, alertId }) => commV3(p).get(`/${pipelineId}/usage-threshold-alert/${alertId}`),
    add: ({ pipelineId }, payload) => commV3(p).post(`/${pipelineId}/usage-threshold-alert`, payload),
    update: ({ pipelineId, alertId }, payload) => commV3(p).put(`/${pipelineId}/usage-threshold-alert/${alertId}`, payload),
    patch: ({ pipelineId, alertId }, payload) => commV3(p).patch(`/${pipelineId}/usage-threshold-alert/${alertId}`, payload),
    delete: ({ pipelineId, alertId }) => commV3(p).delete(`/${pipelineId}/usage-threshold-alert/${alertId}`)
  },
  account: {
    get: () => commV3(p).get('/account'),
    patch: (payload) => commV3(p).patch('/account', payload)
  },
  localDeploy: {
    list: () => commV3(p).get('/account/local-deploy/heartbeat'),
    delete: (edgeId) => commV3(p).delete(`/account/local-deploy/heartbeat/${edgeId}`)
  },
  stateVariables: {
    list: () => commV3(p).get('/state-variable'),
    get: stateVariableId => commV3(p).get(`/state-variable/${stateVariableId}`),
    add: (payload) => commV3(p).post('/state-variable', payload),
    update: (stateVariableId, payload) => commV3(p).put(`/state-variable/${stateVariableId}`, payload),
    patch: (payload) => commV3(p).patch('/state-variable', payload)
  },
  // Node the use of the AI comm endpoint
  ai: {
    expressions: {
      post: (payload) => commV3(p).post('/ai/expression', payload)
    },
    autoParse: {
      post: (payload, options) => commV3(p).post('/ai/auto-parse', payload, options)
    }
  }
}
